import { useState, type ReactNode } from 'react'
import { X } from 'lucide-react'

import { formatRelativeTime } from '@/lib/relative-time'
import { cn } from '@/lib/utils'
import { DataGrid } from '@/modules/data-grid'
import { Avatar } from '@/ui/avatar'
import { buttonVariants } from '@/ui/button-variants'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/ui/dialog'
import { useAssistantBrowserCommand } from '@/lib/assistant-browser-commands'
import { assistantBrowserCommands } from '@/lib/assistant-browser-commands.generated'

import { useSettingHistoryGrid } from '../lib/setting-queries'
import type { ResourceDefaultSettingTarget } from '../lib/setting-target'
import {
  changeLocation,
  deriveRows,
  parseChangeDetail,
  propertyLabel,
  settingLabel,
  type DetailRow,
} from './setting-history-model'

const NO_DETAIL = { messageKey: '', params: {} }

const COLUMN_HEAD =
  'text-muted-foreground text-[0.625rem] font-semibold tracking-wider uppercase'

type SettingHistoryDialogProps = {
  target: ResourceDefaultSettingTarget
  open: boolean
  onOpenChange: (open: boolean) => void
  settingLabel?: string
  description?: ReactNode
  formatValue?: (value: unknown) => string
}

export function SettingHistoryDialog({
  target,
  open,
  onOpenChange,
  settingLabel: customLabel,
  description,
  formatValue,
}: SettingHistoryDialogProps) {
  const resolvedLabel = customLabel ?? settingLabel(target.settingKey)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col gap-0 overflow-hidden p-0">
        <div className="border-border flex items-start justify-between gap-4 border-b px-5 py-4">
          <div className="flex flex-col gap-0.5">
            <DialogTitle>Settings change history</DialogTitle>
            <DialogDescription>
              {description ?? `Changes affecting ${resolvedLabel} at this entity, newest first.`}
            </DialogDescription>
          </div>
          <DialogClose
            aria-label="Close"
            className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}
          >
            <X />
          </DialogClose>
        </div>
        {open ? <SettingHistoryGrid target={target} formatValue={formatValue} /> : null}
        <DialogFooter className="items-center justify-end gap-4 px-5 py-3">
          <DialogClose className={buttonVariants({ size: 'sm' })}>
            Done
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SettingHistoryGrid({
  target,
  formatValue,
}: {
  target: ResourceDefaultSettingTarget
  formatValue?: (value: unknown) => string
}) {
  const grid = useSettingHistoryGrid(target)
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({})
  type Row = (typeof grid.items)[number]
  const detailOf = (row: Row) =>
    row.values.detail === undefined ? NO_DETAIL : parseChangeDetail(row.values.detail)
  const rowsOf = (row: Row) => deriveRows(
    detailOf(row)?.params ?? {},
    propertyLabel,
    formatValue,
  )
  const hasDetail = (row: Row) => detailOf(row) === null || rowsOf(row).length > 0
  const newest = grid.items.reduce<Row | undefined>(
    (latest, row) =>
      !latest || Date.parse(row.values.timestamp ?? '') > Date.parse(latest.values.timestamp ?? '') ? row : latest,
    undefined,
  )?.key
  const isExpanded = (row: Row) => openRows[row.key] ?? row.key === newest

  useAssistantBrowserCommand(assistantBrowserCommands.settingHistory, ({ activityId, expanded }) => {
    const row = grid.items.find((item) => item.key === activityId)
    if (!row || !hasDetail(row)) return false
    setOpenRows((current) => ({ ...current, [activityId]: expanded }))
    return true
  })

  return (
    <div className="flex min-h-96 flex-1 flex-col overflow-hidden px-5 pt-3">
      <DataGrid
        grid={grid}
        caption="Setting changes"
        noun="changes"
        singularNoun="change"
        searchLabel="changes"
        assistantFilter="settings-history"
        columnChooserColumns={['change', 'actor', 'source', 'timestamp']}
        emptyMessage={grid.activeCriteria.search || grid.activeCriteria.filter?.length
          ? 'No change events match your filters.'
          : 'No history yet.'}
        renderers={{
          change: { cell: ({ value, row }) => {
            const rows = rowsOf(row)
            return (
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-foreground text-sm font-medium">{value}</span>
                {rows.length > 0 ? (
                  <span>
                    {isFirstValue(rows) ? 'Initial value' : rows.length === 1 ? '1 change' : `${rows.length} changes`}
                  </span>
                ) : null}
              </div>
            )
          } },
          actor: { cell: ({ value }) => (
            <div className="flex min-w-0 items-center gap-2">
              <Avatar name={value ?? ''} />
              <span className="text-foreground truncate font-medium">{value}</span>
            </div>
          ) },
          source: { cell: ({ value, row }) =>
            changeLocation(detailOf(row) ?? NO_DETAIL, value ?? '', row.values.sourceEntity ?? null) },
          timestamp: { cell: ({ value }) => value === undefined ? null : (
            <div className="flex flex-col">
              <time dateTime={value} title={new Date(value).toLocaleString()} className="text-foreground font-medium">
                {new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </time>
              <span>{formatRelativeTime(value)}</span>
            </div>
          ) },
        }}
        rowDetail={{
          hasDetail,
          isExpanded,
          onToggle: (row) => setOpenRows((current) => ({ ...current, [row.key]: !isExpanded(row) })),
          label: (row) => `Details: ${row.values.change} by ${row.values.actor}, ${new Date(row.values.timestamp ?? '').toLocaleString()}`,
          render: (row) => {
            if (detailOf(row) === null) {
              return <p className="text-muted-foreground">Couldn’t read the details of this change.</p>
            }
            const rows = rowsOf(row)
            return (
              <div className="flex flex-col gap-2">
                {isFirstValue(rows) ? (
                  <p className="text-muted-foreground">
                    First recorded value for this setting here, so there is no
                    previous value to compare against.
                  </p>
                ) : null}
                <ChangeTable rows={rows} />
              </div>
            )
          },
        }}
      />
    </div>
  )
}

function isFirstValue(rows: DetailRow[]) {
  return rows.length > 0 && rows.every((row) => row.before === undefined)
}

function ChangeTable({ rows }: { rows: DetailRow[] }) {
  const hasPrevious = rows.some((r) => r.before !== undefined)
  return (
    <div className="border-border overflow-hidden rounded-md border">
      <table className="w-full text-xs">
        <caption className="sr-only">Changed properties</caption>
        <thead>
          <tr className="bg-muted/50 border-border border-b text-left">
            <th scope="col" className={cn(COLUMN_HEAD, 'px-3 py-1.5')}>
              Property
            </th>
            {hasPrevious && (
              <th scope="col" className={cn(COLUMN_HEAD, 'px-3 py-1.5')}>
                Previous
              </th>
            )}
            <th scope="col" className={cn(COLUMN_HEAD, 'px-3 py-1.5')}>
              {hasPrevious ? 'New' : 'Value'}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.property}
              className="border-border border-b last:border-b-0"
            >
              <td className="text-muted-foreground w-1/3 px-3 py-1.5">
                {row.property}
              </td>
              {hasPrevious && (
                <td className="w-1/3 px-3 py-1.5">
                  {row.before === undefined ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <span className="text-destructive break-all line-through">
                      {row.before}
                    </span>
                  )}
                </td>
              )}
              <td className="w-1/3 px-3 py-1.5">
                {row.after === undefined ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <span className="break-all">{row.after}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
